import { defineComponent, h } from 'vue';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { find, svg, html } from 'property-information';
import deepmerge from 'deepmerge';

const vueMarkdownImpl = defineComponent({
  name: "VueMarkdown",
  props: {
    markdown: {
      type: String,
      default: ""
    },
    customAttrs: {
      type: Object,
      default: () => ({})
    },
    remarkPlugins: {
      type: Array,
      default: () => []
    },
    rehypePlugins: {
      type: Array,
      default: () => []
    },
    rehypeOptions: {
      type: Object,
      default: () => ({})
    },
    sanitize: {
      type: Boolean,
      default: false
    },
    sanitizeOptions: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props, { slots, attrs }) {
    const processor = unified().use(remarkParse).use(props.remarkPlugins).use(remarkRehype, { allowDangerousHtml: true, ...props.rehypeOptions || {} }).use(props.rehypePlugins).use(
      props.sanitize ? [
        [
          rehypeSanitize,
          deepmerge(
            defaultSchema,
            props.sanitizeOptions.sanitizeOptions || {},
            props.sanitizeOptions.mergeOptions || {}
          )
        ]
      ] : []
    );
    const computeCustomAttrs = (node, aliasList, combinedAttrs) => {
      for (let i = aliasList.length - 1; i >= 0; i--) {
        const name = aliasList[i];
        if (name in props.customAttrs) {
          const customAttrs = props.customAttrs[name];
          return typeof customAttrs === "function" ? customAttrs(node, combinedAttrs) : customAttrs;
        }
      }
      return {};
    };
    const parseChildren = (nodeList, context, parent) => {
      const keyCounter = {};
      return nodeList.map((node) => {
        const aliasList = [];
        let attrs2 = {};
        const props2 = {};
        const thisContext = { ...context };
        switch (node.type) {
          case "text":
            return [node.value];
          case "element": {
            aliasList.push(node.tagName);
            keyCounter[node.tagName] = node.tagName in keyCounter ? keyCounter[node.tagName] + 1 : 0;
            props2.key = `${node.tagName}-${keyCounter[node.tagName]}`;
            node.properties = node.properties || {};
            if (node.tagName === "svg") {
              thisContext.svg = true;
            }
            attrs2 = Object.entries(node.properties).reduce((acc, [hastKey, value]) => {
              const attrInfo = find(thisContext.svg ? svg : html, hastKey);
              acc[attrInfo.attribute] = value;
              return acc;
            }, {});
            switch (node.tagName) {
              case "h1":
              case "h2":
              case "h3":
              case "h4":
              case "h5":
              case "h6":
                props2.level = parseFloat(node.tagName.slice(1));
                aliasList.push("heading");
                break;
              case "code":
                props2.languageOriginal = Array.isArray(attrs2["class"]) ? attrs2["class"].find((cls) => cls.startsWith("language-")) : "";
                props2.language = props2.languageOriginal ? props2.languageOriginal.replace("language-", "") : "";
                props2.inline = "tagName" in parent && parent.tagName !== "pre";
                props2.content = node.children[0].value;
                aliasList.push(props2.inline ? "inline-code" : "block-code");
                break;
              case "thead":
              case "tbody":
                thisContext.currentContext = node.tagName;
                break;
              case "td":
              case "th":
              case "tr":
                props2.isHead = context.currentContext === "thead";
                break;
              case "ul":
              case "ol":
                thisContext.listDepth = context.listDepth + 1;
                thisContext.listOrdered = node.tagName === "ol";
                thisContext.listItemIndex = -1;
                props2.ordered = thisContext.listOrdered;
                props2.depth = thisContext.listDepth;
                aliasList.push("list");
                break;
              case "li":
                thisContext.listItemIndex++;
                props2.ordered = thisContext.listOrdered;
                props2.depth = thisContext.listDepth;
                props2.index = thisContext.listItemIndex;
                aliasList.push("list-item");
                break;
              case "slot":
                if (typeof node.properties["slot-name"] === "string") {
                  aliasList.push("s-" + node.properties["slot-name"]);
                  delete node.properties["slot-name"];
                }
                break;
            }
            break;
          }
          default:
            return null;
        }
        attrs2 = {
          ...attrs2,
          ...computeCustomAttrs(node, aliasList, { ...attrs2, ...props2 })
        };
        for (let i = aliasList.length - 1; i >= 0; i--) {
          const targetSlot = slots[aliasList[i]];
          const targetSlotRender = typeof targetSlot === "function" ? targetSlot : null;
          if (targetSlotRender) {
            return targetSlotRender({
              ...attrs2,
              ...props2,
              children: () => parseChildren(node.children, thisContext, node)
            });
          }
        }
        return h(node.tagName, attrs2, parseChildren(node.children, thisContext, node));
      });
    };
    return () => {
      const mdast = processor.parse(props.markdown);
      const hast = processor.runSync(mdast);
      return h(
        "div",
        attrs,
        parseChildren(hast.children, { listDepth: -1, listOrdered: false, listItemIndex: -1, svg: false }, hast)
      );
    };
  }
});
const VueMarkdown = vueMarkdownImpl;

export { VueMarkdown };
